import type {
	SettingsTemplateApplyRequestModel,
	SettingsTemplateCreateModel,
	SettingsTemplateModel,
	SettingsTemplateUpdateModel,
} from '@/shared/generated'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { useMutation } from '@tanstack/react-query'

import { client } from '@/shared/api/client'

export const settingsTemplateMutations = {
	create: () => ({
		mutationKey: ['settings-templates-create'],
		mutationFn: (data: SettingsTemplateCreateModel) =>
			client.settingsTemplate.settingsTemplateCreate(data),
	}),
	update: (settingsTemplateId: string) => ({
		mutationKey: ['settings-templates-update', settingsTemplateId],
		mutationFn: (data: SettingsTemplateUpdateModel) =>
			client.settingsTemplate.settingsTemplateUpdate(
				{ settingsTemplateId },
				data
			),
	}),
	delete: () => ({
		mutationKey: ['settings-templates-delete'],
		mutationFn: (settingsTemplateId: string) =>
			client.settingsTemplate.settingsTemplateDelete({ settingsTemplateId }),
	}),
	apply: () => ({
		mutationKey: ['settings-templates-apply'],
		mutationFn: ({ id, data }: PayloadType) =>
			client.settingsTemplate.settingsTemplateApply(
				{ settingsTemplateId: id },
				data
			),
	}),
}
export function useCreateSettingsTemplateMutation(
	options?: UseMutationOptions<
		SettingsTemplateModel,
		AxiosError,
		SettingsTemplateCreateModel
	>
) {
	return useMutation<
		SettingsTemplateModel,
		AxiosError,
		SettingsTemplateCreateModel
	>({
		...settingsTemplateMutations.create(),
		...(options || {}),
	})
}
export function useUpdateSettingsTemplateMutation(
	settingsTemplateId: string,
	options?: UseMutationOptions<
		SettingsTemplateModel,
		AxiosError,
		SettingsTemplateUpdateModel
	>
) {
	return useMutation<
		SettingsTemplateModel,
		AxiosError,
		SettingsTemplateUpdateModel
	>({
		...settingsTemplateMutations.update(settingsTemplateId),
		...(options || {}),
	})
}
export function useDeleteSettingsTemplateMutation(
	options?: UseMutationOptions<void, AxiosError, string>
) {
	return useMutation<void, AxiosError, string>({
		...settingsTemplateMutations.delete(),
		...(options || {}),
	})
}

type PayloadType = {
	id: string
	data: SettingsTemplateApplyRequestModel
}
export function useApplySettingsTemplateMutation(
	options?: UseMutationOptions<void, AxiosError, PayloadType>
) {
	return useMutation<void, AxiosError, PayloadType>({
		...settingsTemplateMutations.apply(),
		...(options || {}),
	})
}
